<script lang="ts">
  // Compact local-race card: top candidates with counted % and a thin bar, the estimate line in purple.
  import type { RaceSummary } from '../../types';
  import { app } from '../../lib/app.svelte';
  import { fmtInt, fmtPct } from '../../lib/format';
  import { cardName, colorVar, hasVotes, isWriteIn, lastSeatText, marginText, resolvePair, winMarks } from '../../lib/race';
  import WinMark from '../atoms/WinMark.svelte';
  import Avatar from '../atoms/Avatar.svelte';
  import PartyBadge from '../atoms/PartyBadge.svelte';
  import StatusPill from '../atoms/StatusPill.svelte';
  import Est from '../atoms/Est.svelte';

  let { race: r }: { race: RaceSummary } = $props();
  const marks = $derived(winMarks(r));
  const shown = $derived(r.choices.filter((c) => !isWriteIn(c) || marks.has(c.choice_id)).slice(0, Math.max(3, r.vote_for + 1)));
  const hidden = $derived(r.choices.length - shown.length);
  const max = $derived(Math.max(1, ...r.choices.map((c) => c.pct)));
  const pair = $derived(resolvePair(r));
  const pc = $derived(r.pct_expected_counted);
  const any = $derived(hasVotes(r));
  const unitWord = $derived(r.unit_kind === 'county' ? 'counties' : 'precincts');
</script>

<a class="card rc" href={app.href(`#/race/${encodeURIComponent(r.race_id)}`)}>
  <header>
    <div class="ttl">
      <h3>{r.title}</h3>
      {#if r.subtitle || r.vote_for > 1}<div class="sub">{[r.subtitle, r.vote_for > 1 ? `vote for ${r.vote_for}` : null].filter(Boolean).join(' · ')}</div>{/if}
    </div>
    <StatusPill race={r} />
  </header>
  <ul>
    {#each shown as c (c.choice_id)}
      <li>
        <Avatar choice={c} size={30} />
        <div class="mid">
          <div class="row">
            <span class="nm">{cardName(c, r.kind)}</span>
            <PartyBadge party={c.party} color={c.color} />
            <WinMark kind={marks.get(c.choice_id)} compact />
            <span class="pct num">{any ? fmtPct(c.pct) : '—'}</span>
          </div>
          <div class="track"><span style="width:{(100 * c.pct) / max}%;background:{colorVar(c)}"></span></div>
        </div>
      </li>
    {/each}
  </ul>
  {#if hidden > 0}<div class="more">+{hidden} more</div>{/if}
  <footer>
    {#if r.show_needle && r.margin && pair}
      {#if r.vote_for > 1}
        <span class="est-text"><b>{lastSeatText(r.margin.median, pair, r.kind)}</b> <Est /></span>
      {:else}
        <span class="est-text">Est. final: <b>{marginText(r.margin.median, pair, r.kind)}</b> <Est /></span>
      {/if}
    {:else if r.reason_no_needle && r.status !== 'uncontested'}
      <span class="muted">{r.reason_no_needle}</span>
    {/if}
    {#if pc}<span class="muted cnt">≈{fmtPct(pc.median, 0)} counted <Est /></span>{:else}<span class="muted">{fmtInt(r.units_reported)}/{fmtInt(r.units_total)} {unitWord}</span>{/if}
  </footer>
</a>

<style>
  .rc { display: flex; flex-direction: column; gap: 12px; color: inherit; text-decoration: none; transition: box-shadow 0.2s, transform 0.2s; }
  .rc:hover { box-shadow: var(--shadow-lg); text-decoration: none; }
  header { display: flex; justify-content: space-between; gap: 10px; align-items: flex-start; }
  h3 { font-size: 1.08rem; line-height: 1.25; }
  .sub { color: var(--ink-3); font-size: 0.8rem; margin-top: 2px; }
  ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
  li { display: flex; gap: 10px; align-items: center; }
  .mid { flex: 1; min-width: 0; }
  .row { display: flex; align-items: center; gap: 6px; font-size: 0.9rem; }
  .nm { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
  .pct { margin-left: auto; font: 700 1.02rem/1 var(--display); }
  .track { height: 6px; border-radius: 999px; background: var(--card-3); margin-top: 6px; overflow: hidden; }
  .track span { display: block; height: 100%; border-radius: 999px; transition: width var(--t-med) ease; }
  .more { font-size: 0.78rem; color: var(--ink-3); margin-top: -4px; }
  .cnt { display: inline-flex; align-items: center; gap: 5px; }
  footer { display: flex; justify-content: space-between; gap: 8px; flex-wrap: wrap; font-size: 0.82rem; border-top: 1px solid var(--line); padding-top: 10px; margin-top: auto; }
</style>
